/**
 * Gemi saati kaydı (M18; ADR 0065): Ship's Clock Log ekranı ve dinlenme ekranlarındaki saat
 * değişikliği metinleri. İngilizce varsayılan, Türkçe ikinci dil.
 */
export const shipClockEn = {
  title: "Ship's Clock Log",
  close: 'Close',
  password: 'Password',
  subtitle:
    "Changes of the ship's clocks by the master's order: the real moment of the change, the old and new UTC offset and the master's approval. Hours of work and rest are always calculated from the real elapsed time.",
  breadcrumb: { home: 'Home', crew: 'Crew', page: "Ship's Clock Log" },
  vessel: 'Vessel',
  now: "Ship's clock now",
  baseZone: 'Vessel time zone',
  baseZoneHint: 'Used until the first approved change.',
  summary: {
    label: 'Clock summary',
    changes: 'Changes',
    pending: 'Awaiting approval',
    upcoming: 'Next 48 hours',
  },
  list: 'Clock changes',
  columns: {
    effective: 'Effective (ship time)',
    change: 'Change',
    offsets: 'UTC offset',
    reason: 'Reason',
    status: 'Status',
    actions: 'Actions',
  },
  status: { pending: 'Awaiting approval', approved: 'Approved', cancelled: 'Cancelled' },
  change: { retard: 'Retarded {{minutes}}', advance: 'Advanced {{minutes}}' },
  minutes: '{{value}} min',
  cancelledBy: 'Cancelled by {{name}}: {{reason}}',
  recordedBy: 'Recorded by {{name}}',
  approvedBy: 'Approved by {{name}}',
  empty: 'No clock changes recorded.',
  emptyText:
    "Until the first approved change, the ship's clock follows the vessel time zone {{zone}}.",
  loadError: "The ship's clock log could not be loaded.",
  readOnly:
    'Clock changes are recorded and approved on board; the office sees them read-only after the next sync.',
  newChange: 'New clock change',
  form: {
    title: 'New clock change',
    date: 'Date (ship time)',
    time: 'Time (ship time before the change)',
    direction: 'Direction',
    retard: 'Retard (clocks back)',
    advance: 'Advance (clocks forward)',
    amount: 'Minutes',
    amountHint: 'e.g. 60, 30 or 20 for one watch of a 3 × 20 min change',
    newOffset: 'New UTC offset',
    reason: 'Reason',
    reasonPlaceholder:
      "e.g. Retard 1 h at midnight, entering the UTC+2 zone (master's night orders)",
    preview: 'At {{time}} ship time the clocks will read {{after}} ({{from}} → {{to}}): {{day}}.',
    approvalNote:
      "The change takes effect only after the master's approval. Plan it in advance so that the master can approve it before the time.",
    midnightHint:
      'Retard at 00:00 or within the day; a change that takes the clocks back past midnight is not accepted.',
    suggestions: 'Suggestions',
    suggestionPort: 'Port {{label}}: {{zone}} ({{offset}})',
    suggestionZone: 'Zone time at the last report position {{label}}: {{offset}}',
    use: 'Use',
    save: 'Record change',
    cancel: 'Cancel',
  },
  approve: {
    action: 'Approve',
    title: 'Approve clock change',
    text: "You approve the change of the ship's clocks at {{time}} ({{from}} → {{to}}) as master's order. Your name, the time and the change are recorded.",
    confirm: 'Approve',
  },
  cancel: {
    action: 'Cancel change',
    title: 'Cancel clock change',
    text: 'The change is kept in the log as cancelled, with your name, the time and the reason.',
    reason: 'Reason',
    confirm: 'Cancel change',
  },
  info: {
    title: 'How it is calculated',
    text: 'Work, rest and overtime are calculated from the real elapsed time (UTC). When the clocks are retarded the day has more than 24 hours and the repeated hour is shown twice; when they are advanced the skipped time is shown hatched. MLC 2006 and STCW limits are checked over any 24 hours and any 7 days, independent of the calendar day.',
  },
  errors: {
    CLOCK_CHANGE_ORDER:
      'Changes are recorded and approved in time order: there is a later change, or an earlier change awaits approval.',
    CLOCK_OFFSET_MISMATCH: "The ship's clock changed meanwhile; reload and try again.",
    CLOCK_RETARD_ACROSS_MIDNIGHT:
      'A retard may take the clocks back past midnight only at 00:00. Choose 00:00 or a time within the day.',
    DATE_LINE_NOT_MIDNIGHT: 'A date line change (12 hours or more) is made at 00:00 ship time.',
    CLOCK_CHANGE_TOO_FAR: 'A change can be planned at most 31 days ahead.',
    CLOCK_RETARD_TOO_LARGE: 'The clocks can be retarded by at most 24 hours.',
    CLOCK_CHANGE_ZERO: 'The new offset is the same as the current one.',
    OFFSET_OUT_OF_RANGE: 'The UTC offset must be between −12:00 and +14:00.',
    REASON_REQUIRED: 'Enter a reason.',
    TOO_LONG: 'The text is too long.',
    CLOCK_CHANGE_NOT_LAST: 'Only the last change in the log can be cancelled.',
    CLOCK_CHANGE_IN_EFFECT:
      'An approved change that has taken effect cannot be cancelled; record a new change instead.',
    CLOCK_CHANGE_CANCELLED: 'This change was cancelled.',
    ALREADY_APPROVED: 'This change is already approved.',
    INVALID_DATE: 'Enter a valid date and time.',
    TIME_SKIPPED: 'This time was not lived on that day.',
    INVALID_CREDENTIALS: 'The password is wrong.',
    REAUTHENTICATION_REQUIRED: 'Enter your password to approve.',
    FORBIDDEN: 'You are not authorized for this action.',
    GENERIC: 'The action could not be completed.',
  },
};

export const shipClockTr: typeof shipClockEn = {
  title: 'Gemi Saati Kaydı',
  close: 'Kapat',
  password: 'Parola',
  subtitle:
    'Gemi saatinin kaptanın emriyle değiştirilmesi: değişikliğin gerçek anı, eski ve yeni UTC farkı ve kaptan onayı. Çalışma ve dinlenme saatleri her zaman gerçekte geçen süreden hesaplanır.',
  breadcrumb: { home: 'Ana Sayfa', crew: 'Personel', page: 'Gemi Saati Kaydı' },
  vessel: 'Gemi',
  now: 'Şu an gemi saati',
  baseZone: 'Geminin saat dilimi',
  baseZoneHint: 'İlk onaylı değişikliğe kadar kullanılır.',
  summary: {
    label: 'Saat özeti',
    changes: 'Değişiklik',
    pending: 'Onay bekleyen',
    upcoming: 'Önümüzdeki 48 saat',
  },
  list: 'Saat değişiklikleri',
  columns: {
    effective: 'Geçerlilik (gemi saati)',
    change: 'Değişiklik',
    offsets: 'UTC farkı',
    reason: 'Açıklama',
    status: 'Durum',
    actions: 'İşlemler',
  },
  status: { pending: 'Onay bekliyor', approved: 'Onaylandı', cancelled: 'İptal edildi' },
  change: { retard: '{{minutes}} geri', advance: '{{minutes}} ileri' },
  minutes: '{{value}} dk',
  cancelledBy: '{{name}} iptal etti: {{reason}}',
  recordedBy: 'Kaydeden: {{name}}',
  approvedBy: 'Onaylayan: {{name}}',
  empty: 'Saat değişikliği kaydı yok.',
  emptyText: 'İlk onaylı değişikliğe kadar gemi saati geminin saat dilimini ({{zone}}) izler.',
  loadError: 'Gemi saati kaydı yüklenemedi.',
  readOnly:
    'Saat değişiklikleri gemide kaydedilir ve onaylanır; ofis bunları sonraki senkrondan sonra salt okunur görür.',
  newChange: 'Yeni saat değişikliği',
  form: {
    title: 'Yeni saat değişikliği',
    date: 'Tarih (gemi saati)',
    time: 'Saat (değişiklikten önceki gemi saati)',
    direction: 'Yön',
    retard: 'Geri al',
    advance: 'İleri al',
    amount: 'Dakika',
    amountHint: 'ör. 60, 30 ya da 3 × 20 dk değişiklikte bir vardiya için 20',
    newOffset: 'Yeni UTC farkı',
    reason: 'Açıklama',
    reasonPlaceholder:
      'ör. Gece yarısı 1 saat geri, UTC+2 bölgesine giriş (kaptanın gece talimatı)',
    preview: 'Gemi saati {{time}} olduğunda {{after}} gösterecek ({{from}} → {{to}}): {{day}}.',
    approvalNote:
      'Değişiklik yalnız kaptanın onayından sonra geçerli olur. Kaptanın vaktinden önce onaylayabilmesi için önceden planlayın.',
    midnightHint:
      'Geri alma saat 00:00 itibarıyla ya da gün içinde yapılır; saati gece yarısının ötesine geri götüren değişiklik kabul edilmez.',
    suggestions: 'Öneriler',
    suggestionPort: '{{label}} limanı: {{zone}} ({{offset}})',
    suggestionZone: 'Son rapor konumunda deniz saati {{label}}: {{offset}}',
    use: 'Kullan',
    save: 'Değişikliği kaydet',
    cancel: 'Vazgeç',
  },
  approve: {
    action: 'Onayla',
    title: 'Saat değişikliğini onayla',
    text: 'Gemi saatinin {{time}} itibarıyla değişmesini ({{from}} → {{to}}) kaptan emri olarak onaylıyorsunuz. Adınız, zaman ve değişiklik kayda yazılır.',
    confirm: 'Onayla',
  },
  cancel: {
    action: 'Değişikliği iptal et',
    title: 'Saat değişikliğini iptal et',
    text: 'Değişiklik kayıtta iptal olarak; adınız, zaman ve gerekçeyle kalır.',
    reason: 'Gerekçe',
    confirm: 'İptal et',
  },
  info: {
    title: 'Nasıl hesaplanır',
    text: 'Çalışma, dinlenme ve fazla mesai gerçekte geçen süreden (UTC) hesaplanır. Saat geri alındığında gün 24 saatten uzundur ve tekrar eden saat iki kez gösterilir; ileri alındığında yaşanmayan süre taralı gösterilir. MLC 2006 ve STCW sınırları takvim gününden bağımsız olarak herhangi 24 saat ve herhangi 7 gün üzerinden denetlenir.',
  },
  errors: {
    CLOCK_CHANGE_ORDER:
      'Değişiklikler zaman sırasıyla kaydedilir ve onaylanır: daha sonraki bir değişiklik var ya da önceki bir değişiklik onay bekliyor.',
    CLOCK_OFFSET_MISMATCH: 'Gemi saati bu arada değişti; yenileyip yeniden deneyin.',
    CLOCK_RETARD_ACROSS_MIDNIGHT:
      'Geri alma saati gece yarısının ötesine yalnız saat 00:00 itibarıyla götürebilir. 00:00’ı ya da gün içinde bir saati seçin.',
    DATE_LINE_NOT_MIDNIGHT:
      'Tarih çizgisi değişikliği (12 saat ve üstü) gemi saatiyle 00:00 itibarıyla yapılır.',
    CLOCK_CHANGE_TOO_FAR: 'Değişiklik en çok 31 gün sonrası için planlanabilir.',
    CLOCK_RETARD_TOO_LARGE: 'Saat en çok 24 saat geri alınabilir.',
    CLOCK_CHANGE_ZERO: 'Yeni fark şimdiki farkla aynı.',
    OFFSET_OUT_OF_RANGE: 'UTC farkı −12:00 ile +14:00 arasında olmalı.',
    REASON_REQUIRED: 'Açıklama girin.',
    TOO_LONG: 'Metin çok uzun.',
    CLOCK_CHANGE_NOT_LAST: 'Yalnız kayıttaki son değişiklik iptal edilebilir.',
    CLOCK_CHANGE_IN_EFFECT:
      'Yürürlüğe girmiş onaylı değişiklik iptal edilemez; yerine yeni bir değişiklik kaydedin.',
    CLOCK_CHANGE_CANCELLED: 'Bu değişiklik iptal edildi.',
    ALREADY_APPROVED: 'Bu değişiklik zaten onaylandı.',
    INVALID_DATE: 'Geçerli bir tarih ve saat girin.',
    TIME_SKIPPED: 'Bu saat o gün yaşanmadı.',
    INVALID_CREDENTIALS: 'Parola yanlış.',
    REAUTHENTICATION_REQUIRED: 'Onaylamak için parolanızı girin.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    GENERIC: 'İşlem tamamlanamadı.',
  },
};

/** Dinlenme ekranları, aylık kayıt ve PDF'teki saat değişikliği metinleri (`restHours.clock`). */
export const restHoursClockEn = {
  dayLength: '{{hours}} h day',
  adjustment: 'Clock adjustment',
  retarded: "Ship's clocks retarded by {{minutes}} min at {{time}} ({{from}} → {{to}})",
  advanced: "Ship's clocks advanced by {{minutes}} min at {{time}} ({{from}} → {{to}})",
  willRetard:
    "Ship's clocks will be retarded by {{minutes}} min on {{date}} at {{time}} ({{from}} → {{to}})",
  willAdvance:
    "Ship's clocks will be advanced by {{minutes}} min on {{date}} at {{time}} ({{from}} → {{to}})",
  pending:
    "A clock change on {{date}} at {{time}} ({{from}} → {{to}}) awaits the master's approval; ship time does not change until it is approved.",
  repeated: '{{from}}–{{to}} lived twice',
  notLived: '{{from}}–{{to}} not lived',
  occurrence1: '(1st)',
  occurrence2: '(2nd)',
  shown: 'Shown {{value}}',
  actual: 'Actual {{value}}',
  dateRepeated: 'Date lived twice (International Date Line)',
  dateSkipped: 'Date skipped (International Date Line)',
  enteredAt: 'Entered at {{offset}}',
  whichTime: 'Which {{time}}?',
  first: '1st',
  second: '2nd',
  formPending:
    "A clock change at {{time}} awaits the master's approval; times are converted with the approved offset {{offset}}.",
  notes: "Ship's clock",
  noteRetarded:
    "Ship's clocks retarded by {{minutes}} minutes on {{date}} {{time}} ({{from}} → {{to}})",
  noteAdvanced:
    "Ship's clocks advanced by {{minutes}} minutes on {{date}} {{time}} ({{from}} → {{to}})",
  noteRepeated: 'Date repeated: {{date}} (International Date Line)',
  noteSkipped: 'Date skipped: {{date}} (International Date Line)',
  skippedDay: 'Not lived',
  log: "Ship's Clock Log",
};

export const restHoursClockTr: typeof restHoursClockEn = {
  dayLength: '{{hours}} saatlik gün',
  adjustment: 'Saat ayarı',
  retarded: 'Gemi saati {{time}} itibarıyla {{minutes}} dk geri alındı ({{from}} → {{to}})',
  advanced: 'Gemi saati {{time}} itibarıyla {{minutes}} dk ileri alındı ({{from}} → {{to}})',
  willRetard:
    'Gemi saati {{date}} {{time}} itibarıyla {{minutes}} dk geri alınacak ({{from}} → {{to}})',
  willAdvance:
    'Gemi saati {{date}} {{time}} itibarıyla {{minutes}} dk ileri alınacak ({{from}} → {{to}})',
  pending:
    '{{date}} {{time}} için girilen saat değişikliği ({{from}} → {{to}}) kaptanın onayını bekliyor; onaylanana kadar gemi saati değişmez.',
  repeated: '{{from}}–{{to}} iki kez yaşandı',
  notLived: '{{from}}–{{to}} yaşanmadı',
  occurrence1: '(1.)',
  occurrence2: '(2.)',
  shown: 'Görünen {{value}}',
  actual: 'Gerçek {{value}}',
  dateRepeated: 'Gün iki kez yaşandı (Tarih Değiştirme Çizgisi)',
  dateSkipped: 'Gün atlandı (Tarih Değiştirme Çizgisi)',
  enteredAt: '{{offset}} ile girildi',
  whichTime: 'Hangi {{time}}?',
  first: '1.',
  second: '2.',
  formPending:
    '{{time}} için girilen saat değişikliği kaptanın onayını bekliyor; saatler onaylı farkla ({{offset}}) çevriliyor.',
  notes: 'Gemi saati',
  noteRetarded:
    'Gemi saati {{date}} {{time}} itibarıyla {{minutes}} dakika geri alındı ({{from}} → {{to}})',
  noteAdvanced:
    'Gemi saati {{date}} {{time}} itibarıyla {{minutes}} dakika ileri alındı ({{from}} → {{to}})',
  noteRepeated: 'Tekrarlanan gün: {{date}} (Tarih Değiştirme Çizgisi)',
  noteSkipped: 'Atlanan gün: {{date}} (Tarih Değiştirme Çizgisi)',
  skippedDay: 'Yaşanmadı',
  log: 'Gemi Saati Kaydı',
};
